import React, { useState, useRef } from "react";
import { convertImageToWebP, formatFileSize } from "../../utils/imageOptimizer";
import "./ImageUploadDropzone.css";

function ImageUploadDropzone({
  value,
  existingUrl,
  onChange,
  onClear,
  label = "Product Image"
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [optStats, setOptStats] = useState(null);
  const fileInputRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith("image/")) {
      alert("Please upload a valid image file (JPG, PNG, WebP, etc.)");
      return;
    }

    setIsProcessing(true);
    try {
      const result = await convertImageToWebP(file);
      setOptStats({
        origSize: result.origSize,
        newSize: result.newSize,
        reduction: result.reduction
      });
      onChange(result.file, result.previewUrl);
    } catch (err) {
      console.error("Error optimizing image:", err);
      // Fallback: pass raw file
      onChange(file, URL.createObjectURL(file));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setOptStats(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (onClear) onClear();
    else onChange(null, null);
  };

  const displayPreview = (value && value.previewUrl) || (value instanceof File ? URL.createObjectURL(value) : null) || existingUrl;

  return (
    <div className="upload-dropzone-container">
      {label && <label className="dropzone-label">{label}</label>}

      <div
        className={`dropzone-box ${isDragging ? "dragging" : ""} ${displayPreview ? "has-preview" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png, image/jpeg, image/jpg, image/webp"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {isProcessing ? (
          <div className="dropzone-processing">
            <div className="dropzone-spinner"></div>
            <p>Optimizing & Converting to WebP...</p>
          </div>
        ) : displayPreview ? (
          <div className="dropzone-preview-wrapper">
            <img
              src={displayPreview}
              alt="Preview"
              className="dropzone-img-preview"
            />
            <div className="dropzone-overlay-actions">
              <span className="dropzone-badge webp-badge">
                <i className="fas fa-bolt"></i> Auto WebP
              </span>
              {optStats && optStats.reduction > 0 && (
                <span className="dropzone-badge saving-badge">
                  -{optStats.reduction}% ({formatFileSize(optStats.origSize)} ➔ {formatFileSize(optStats.newSize)})
                </span>
              )}
              <button
                type="button"
                className="dropzone-btn-remove"
                onClick={handleRemove}
                title="Remove image"
              >
                <i className="fas fa-trash-alt"></i> Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="dropzone-placeholder">
            <div className="dropzone-icon-circle">
              <i className="fas fa-cloud-upload-alt"></i>
            </div>
            <div className="dropzone-text">
              <p className="dropzone-title">Click to upload or drag & drop</p>
              <p className="dropzone-hint">
                PNG, JPG, or WebP (Automatically converted to optimized .webp)
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageUploadDropzone;
